/** Public contract for incrementally verified, uncapped Burnout runs. */

import { PAD_SCHEMES } from "@lache/input/schemes";
import {
  BTN_RESTART,
  BURNOUT_VERSION,
  defaultTuning,
  HZ,
  type TapeFrame,
  type Tuning,
  tuningHash,
} from "@lache/sim";
import { ScoreError } from "./leaderboard";
import { cleanPlayerName, EXPLICIT_NAME_ERROR, isExplicitName } from "./names";

export { BURNOUT_VERSION };
export const BURNOUT_TUNING = tuningHash(defaultTuning);
/** Compatible hurdle improvements retain the published course board and in-flight session routes. */
const preFlowTuning: Tuning = { ...defaultTuning, hurdleFlow: 0 };
export const BURNOUT_KEY_TUNING = tuningHash(preFlowTuning);
const acceptedTuning = new Map<string, { tuning: Tuning; hash: string }>();
for (const tuning of [preFlowTuning, defaultTuning]) {
  const family = { tuning, hash: tuningHash(tuning) };
  for (let padScheme = 0; padScheme < PAD_SCHEMES.length; padScheme++)
    acceptedTuning.set(tuningHash({ ...tuning, padScheme }), family);
}
/** Only exact published default-physics families qualify for the shared Burnout board. */
export function burnoutPhysics(hash: string): { tuning: Tuning; hash: string } | undefined {
  return acceptedTuning.get(hash);
}
export const MAX_BURNOUT_CHUNK_TICKS = 10 * HZ;
export const MAX_BURNOUT_CHUNK_BYTES = 192 * 1024;
export const BURNOUT_BOARD_SIZE = 10;
export const BURNOUT_SESSION_TTL_MS = 24 * 60 * 60 * 1000;
export const BURNOUT_START_INTERVAL_MS = 2000;
export const BURNOUT_CHUNK_INTERVAL_MS = 100;
export const BURNOUT_ACTIVE_SESSIONS = 4;
/** Persisted terminal entries retain their original board when the current layout advances. */
export function burnoutBoardKey(version: number, tuning: string): string {
  return `board:${version}:${burnoutPhysics(tuning) ? BURNOUT_KEY_TUNING : tuning}`;
}
export const BURNOUT_BOARD_KEY = burnoutBoardKey(BURNOUT_VERSION, BURNOUT_TUNING);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface BurnoutScore {
  name: string;
  obstacles: number;
  /** Seconds to the last cleared obstacle: waiting after it cannot improve a score. */
  time: number;
  /** Total simulated seconds up to and including the first failure. */
  duration: number;
  date: number;
  achievedAt?: number;
  version: number;
  tuning: string;
}
export interface BurnoutBoard {
  version: number;
  tuning: string;
  entries: BurnoutScore[];
}
export interface BurnoutStart {
  runId: string;
  playerToken: string;
  name: string;
  version: number;
  tuningHash: string;
}
export interface BurnoutChunk {
  version: number;
  playerToken: string;
  sequence: number;
  frames: TapeFrame[];
  achievedAt?: number;
}
export interface BurnoutAcknowledgement {
  runId: string;
  nextSequence: number;
  failed: boolean;
  obstacles: number;
  time: number;
  duration: number;
  entry?: BurnoutScore;
}
export interface BurnoutError {
  error: string;
  status: number;
  retryAfter?: number;
}
export type BurnoutResult = BurnoutAcknowledgement | BurnoutError;

export function burnoutRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function burnoutId(value: unknown, kind = "run id"): string {
  if (typeof value !== "string" || !UUID.test(value)) throw new ScoreError(`invalid ${kind}`);
  return value.toLowerCase();
}
export function validateBurnoutStart(value: unknown): BurnoutStart {
  if (!burnoutRecord(value)) throw new ScoreError("expected a Burnout run");
  const runId = burnoutId(value.runId);
  const playerToken = burnoutId(value.playerToken, "player token");
  if (typeof value.name !== "string" || value.name.length > 256) throw new ScoreError("invalid player name");
  if (isExplicitName(value.name)) throw new ScoreError(EXPLICIT_NAME_ERROR);
  if (value.version !== BURNOUT_VERSION)
    throw new ScoreError("Burnout has changed; reload and run again", 410);
  const family = typeof value.tuningHash === "string" ? burnoutPhysics(value.tuningHash) : undefined;
  if (!family) throw new ScoreError("Burnout has changed; reload and run again", 409);
  return {
    runId,
    playerToken,
    name: cleanPlayerName(value.name),
    version: BURNOUT_VERSION,
    tuningHash: family.hash,
  };
}
export function validateBurnoutChunk(value: unknown): BurnoutChunk {
  if (!burnoutRecord(value)) throw new ScoreError("expected Burnout inputs");
  // The original stream omitted its layout version. Never replay those inputs on newer geometry.
  if ((value.version ?? 1) !== BURNOUT_VERSION)
    throw new ScoreError("Burnout has changed; reload and run again", 410);
  const playerToken = burnoutId(value.playerToken, "player token");
  if (typeof value.sequence !== "number" || !Number.isSafeInteger(value.sequence) || value.sequence < 0)
    throw new ScoreError("invalid chunk sequence");
  if (
    !Array.isArray(value.frames) ||
    value.frames.length === 0 ||
    value.frames.length > MAX_BURNOUT_CHUNK_TICKS
  )
    throw new ScoreError("invalid Burnout input frames");
  const frames: TapeFrame[] = [];
  let ticks = 0;
  for (const raw of value.frames) {
    if (!Array.isArray(raw) || raw.length !== 8 || !raw.every(Number.isInteger))
      throw new ScoreError("input frames must contain eight integers");
    const frame = raw as TapeFrame;
    if (
      frame[0] < 1 ||
      frame[0] > MAX_BURNOUT_CHUNK_TICKS ||
      frame.slice(1, 5).some((v) => v < -127 || v > 127) ||
      frame[5] < 0 ||
      frame[5] > 255 ||
      frame[6] < 0 ||
      frame[6] > 255 ||
      frame[7] < 0 ||
      frame[7] > 15
    )
      throw new ScoreError("input is outside the controller range");
    if ((frame[7] & BTN_RESTART) !== 0) throw new ScoreError("a restart must begin a new Burnout run");
    ticks += frame[0];
    if (ticks > MAX_BURNOUT_CHUNK_TICKS) throw new ScoreError("Burnout input chunk is too long");
    frames.push([...frame]);
  }
  const achievedAt =
    typeof value.achievedAt === "number" &&
    Number.isSafeInteger(value.achievedAt) &&
    value.achievedAt >= 0 &&
    value.achievedAt <= 8_640_000_000_000_000
      ? value.achievedAt
      : undefined;
  return {
    version: BURNOUT_VERSION,
    playerToken,
    sequence: value.sequence,
    frames,
    ...(achievedAt === undefined ? {} : { achievedAt }),
  };
}
export function publicBurnoutScore(score: BurnoutScore): BurnoutScore {
  return {
    name: cleanPlayerName(score.name),
    obstacles: score.obstacles,
    time: score.time,
    duration: score.duration,
    date: score.date,
    ...(score.achievedAt === undefined ? {} : { achievedAt: score.achievedAt }),
    version: score.version,
    tuning: score.tuning,
  };
}
export function compareBurnoutScores(a: BurnoutScore, b: BurnoutScore): number {
  return b.obstacles - a.obstacles || a.time - b.time || a.date - b.date;
}

/** Bounded stream read even without a truthful Content-Length. */
export async function readBurnoutBody(request: Request): Promise<unknown> {
  if (Number(request.headers.get("Content-Length")) > MAX_BURNOUT_CHUNK_BYTES)
    throw new ScoreError("Burnout request is too large", 413);
  if (!request.body) throw new ScoreError("missing Burnout request");
  const reader = request.body.getReader();
  const parts: Uint8Array[] = [];
  let size = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    size += value.byteLength;
    if (size > MAX_BURNOUT_CHUNK_BYTES) {
      await reader.cancel();
      throw new ScoreError("Burnout request is too large", 413);
    }
    parts.push(value);
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    bytes.set(part, offset);
    offset += part.length;
  }
  try {
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new ScoreError("invalid Burnout JSON");
  }
}
